import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, toast, Toggle } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useUser } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';

function InternalSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const [hasDarkMode, toggleDarkMode] = useToggle(user.getSetting('dark_mode') ?? false);
  const [hasDevelopmentNav, toggleDevelopmentNav] = useToggle(
    user.getSetting('show_dev_nav') ?? false,
  );

  async function handleChangeDevelopmentNav(value?: boolean): Promise<void> {
    user.setSetting('show_dev_nav', value);
    try {
      await user.patchDirty();
      toast(
        t('Development navigation toggled to {{value}}', {
          value: value === true ? 'enabled' : 'disabled',
        }),
      );
    } catch (error) {
      toast(t('Failed to toggle development navigation\n{{- error}}', { error: error }));
    }

    toggleDevelopmentNav();
  }

  async function handleChangeDarkMode(value?: boolean): Promise<void> {
    user.setSetting('dark_mode', value);
    try {
      await user.patchDirty();
      toast(t('Switched to {{mode}} mode', { mode: value === true ? 'dark' : 'light' }));
    } catch (error) {
      toast(
        t('Failed to switch to {{mode}} mode\n{{- error}}', {
          mode: value === true ? 'dark' : 'light',
          error: error,
        }),
      );
    }

    window.Appcues?.track('Toggled dark mode', {
      new_mode: value === true ? 'dark' : 'light',
    });

    toggleDarkMode();
  }

  return (
    <Page title={'Internal settings'}>
      {user.isSuperuser && (
        <Card>
          <Card.Header title={t('Development Nav')} />
          <Card.Content
            addVerticalGap={true}
            description={t(
              'Show "Development & QA" nav item. This is only available for users with the Superuser role.',
            )}
          >
            <Toggle
              label={t('Development Nav')}
              layout={'well'}
              name={'development-nav-toggle'}
              onChange={handleChangeDevelopmentNav}
              value={hasDevelopmentNav}
            />
          </Card.Content>
        </Card>
      )}
      <Card>
        <Card.Header title={t('Dark Mode (alpha)')} />
        <Card.Content
          addVerticalGap={true}
          description={t(
            'Try dark mode. This mode is only available for internal Feathr accounts.',
          )}
        >
          <Toggle
            label={t('Dark mode')}
            layout={'well'}
            name={'dark-mode-toggle'}
            onChange={handleChangeDarkMode}
            value={hasDarkMode}
          />
        </Card.Content>
      </Card>
    </Page>
  );
}

export default observer(InternalSettingsPage);
